import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { deduplicate } from '@klareospace/pharos-lib-utils';
import type { SiteWithSpaceName, SpaceWithSiteCount } from '@klareospace/pharos-lib-types';

import { useSpaces } from '../../../settings/spaces/hooks/use-spaces';
import { useSites } from '../../../settings/sites/hooks/use-sites';
import { ResultsList } from './results-list';

// ─── Constants ────────────────────────────────────────────────────────────────

// Sane cap for the parent-spaces lookup — bounded by the (small) site result page size.
const PARENT_SPACES_PAGE_SIZE = 10;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface JumpToResultsProps {
  onBrowseSpace: (spaceId: string, spaceName: string) => void;
  onClose: () => void;
  query: string;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

interface JumpToResultsData {
  isEmpty: boolean;
  isLoading: boolean;
  sites: SiteWithSpaceName[];
  spaces: SpaceWithSiteCount[];
}

/**
 * Fetches name-matched spaces and sites for the query, then also fetches the parent
 * spaces of the matched sites so a site-only match still surfaces its space (e.g.
 * searching "paris" finds PARIS 15 whose parent Île-de-France doesn't match "paris").
 */
function useJumpToResultsData(query: string): JumpToResultsData {
  const { data: spacesData, isLoading: isSpacesLoading } = useSpaces({
    enabled: query.length > 0,
    name: query,
    page: 1,
    pageSize: 3,
  });
  const { data: sitesData, isLoading: isSitesLoading } = useSites({
    enabled: query.length > 0,
    name: query,
    page: 1,
    pageSize: 3,
  });

  const nameMatchedSpaces = spacesData?.items ?? [];
  const sites = sitesData?.items ?? [];
  const parentSpaceIds = deriveMissingParentSpaceIds(sites, nameMatchedSpaces);

  const { data: parentSpacesData, isLoading: isParentSpacesLoading } = useSpaces({
    enabled: parentSpaceIds.length > 0,
    ids: parentSpaceIds,
    page: 1,
    pageSize: PARENT_SPACES_PAGE_SIZE,
  });

  const spaces = mergeSpaces(nameMatchedSpaces, parentSpacesData?.items ?? []);

  return {
    isEmpty: spaces.length === 0 && sites.length === 0,
    isLoading: isSpacesLoading || isSitesLoading || isParentSpacesLoading,
    sites,
    spaces,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function JumpToResults({ onBrowseSpace, onClose, query }: JumpToResultsProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { isEmpty, isLoading, sites, spaces } = useJumpToResultsData(query);

  if (isLoading) {
    return (
      <div className="px-3 py-4">
        <span className="text-sm text-muted-foreground">{t('mainSelector.searching')}</span>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="px-3 py-4">
        <span className="text-sm text-muted-foreground">{t('address.emptyResults')}</span>
      </div>
    );
  }

  return <ResultsList onBrowseSpace={onBrowseSpace} onClose={onClose} sites={sites} spaces={spaces} />;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parent space ids of the matched sites, excluding spaces already found by name.
 * Ensures the Spaces section also surfaces the parent of a site-only match.
 */
function deriveMissingParentSpaceIds(sites: SiteWithSpaceName[], nameMatchedSpaces: SpaceWithSiteCount[]): string[] {
  const nameMatchedIds = new Set(nameMatchedSpaces.map((space) => space.id));
  const missingSpaceIds = sites.map((site) => site.spaceId).filter((spaceId) => !nameMatchedIds.has(spaceId));
  return deduplicate(missingSpaceIds);
}

/** Name-matched spaces first, then parent spaces of matched sites, deduped by id. */
function mergeSpaces(
  nameMatchedSpaces: SpaceWithSiteCount[],
  parentSpaces: SpaceWithSiteCount[],
): SpaceWithSiteCount[] {
  const nameMatchedIds = new Set(nameMatchedSpaces.map((space) => space.id));
  const missingParentSpaces = parentSpaces.filter((space) => !nameMatchedIds.has(space.id));
  return [...nameMatchedSpaces, ...missingParentSpaces];
}
